import { AuthenticationService } from "../../core/authenticationService/AuthenticationService";
import { ImageUploadService } from "../../core/imageUploadService/ImageUploadService";
import { UserService } from "../../core/userService/UserService";
import { CoreError, CoreErrorCause } from "../models/CoreError";
import { Gender } from "../models/Gender";
import { OnboardingState } from "../models/OnboardingState";
import { User, UserHelper } from "../models/User";

export class OnboardingFeature {
  private static readonly DEFAULT_LOAD_USER_OPTIONS = {
    suppressMissingProfileReporting: false,
  };

  private static readonly DEFAULT_REQUIRED_ONBOARDING_OPTIONS = {
    suppressMissingProfileReporting: false,
  };

  private static getAuthUserIdOrThrow(): string {
    const userId = AuthenticationService.getInstance().userId;
    if (!userId) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: "No authenticated user available for onboarding",
      });
    }
    return userId;
  }

  public static async loadCurrentUser(options?: {
    suppressMissingProfileReporting?: boolean;
  }): Promise<User> {
    const userId = this.getAuthUserIdOrThrow();
    const resolvedOptions = {
      ...this.DEFAULT_LOAD_USER_OPTIONS,
      ...options,
    };
    return UserService.getInstance().loadUser(userId, resolvedOptions);
  }

  public static async getRequiredOnboardingState(options?: {
    suppressMissingProfileReporting?: boolean;
  }): Promise<OnboardingState | null> {
    const resolvedOptions = {
      ...this.DEFAULT_REQUIRED_ONBOARDING_OPTIONS,
      ...options,
    };
    const user = await this.loadCurrentUser(resolvedOptions);
    return UserHelper.isOnboardedCorrectly(user);
  }

  public static async getInitialOnboardingState(): Promise<OnboardingState | null> {
    // During onboarding entry a missing profile is the known signup race
    // (auth user exists before users/<uid>/profile is written) — expected,
    // handled by the caller's fallback, not a Sentry error.
    const user = await this.loadCurrentUser({
      suppressMissingProfileReporting: true,
    });

    if (!user.fullName || user.fullName.trim().length === 0) {
      return OnboardingState.Name;
    }

    if (!user.profilePhotoURL) {
      return OnboardingState.ProfilePhoto;
    }

    if (user.gender === Gender.Unknown) {
      return OnboardingState.Gender;
    }

    return null;
  }

  public static async saveName(fullName: string): Promise<void> {
    const userId = this.getAuthUserIdOrThrow();
    await UserService.getInstance().updateUserName(userId, fullName.trim());
  }

  public static async saveProfilePhoto(file: File): Promise<string> {
    const userId = this.getAuthUserIdOrThrow();
    const photoUrl = await ImageUploadService.getInstance().uploadProfilePhoto(
      file,
      userId,
    );
    await UserService.getInstance().updateUserProfilePhoto(userId, photoUrl);
    return photoUrl;
  }

  public static async saveGender(gender: Gender): Promise<void> {
    const userId = this.getAuthUserIdOrThrow();
    await UserService.getInstance().updateUserGender(userId, gender);
  }

  public static async completeOnboarding(): Promise<void> {
    const userId = this.getAuthUserIdOrThrow();
    await UserService.getInstance().updateUser(userId, {
      didFinishOnboarding: true,
      didPassTutorialCards: false,
    });
  }
}
